import type {DownloadOptions} from '../types/file';
import {skipBeforeUnloadOnce} from './beforeUnloadGuard';
import {isValidDownloadUrl} from './validators';
import message from '@/components/common/Message';

const EXT_PATTERN = /\.[a-z0-9]+$/i;

/** 从 URL pathname 中提取文件名 */
function parseFileNameFromUrl(url: string): string {
    try {
        const pathname = new URL(url).pathname;
        const segments = pathname.split('/');
        const last = segments[segments.length - 1];
        return last ? decodeURIComponent(last) : 'download';
    } catch {
        return 'download';
    }
}

function getExtensionFromUrl(url: string): string {
    const fileName = parseFileNameFromUrl(url);
    const match = EXT_PATTERN.exec(fileName);
    return match?.[0] ?? '';
}

function resolveFileName(url: string, fileName?: string): string {
    const resolvedFileName = fileName ?? parseFileNameFromUrl(url);
    if (!resolvedFileName || EXT_PATTERN.test(resolvedFileName)) {
        return resolvedFileName;
    }

    const ext = getExtensionFromUrl(url);
    return ext ? `${resolvedFileName}${ext}` : resolvedFileName;
}

function createAttachmentDisposition(fileName: string): string {
    return `attachment; filename*=UTF-8''${encodeURIComponent(fileName)}`;
}

function createDownloadUrl(url: string, fileName: string, forceAttachment?: boolean): string {
    if (forceAttachment === false) {
        return url;
    }

    const downloadUrl = new URL(url);
    downloadUrl.searchParams.set(
        'responseContentDisposition',
        createAttachmentDisposition(fileName),
    );
    return downloadUrl.toString();
}

/**
 * 通用文件下载（原生 <a> 触发浏览器下载）
 *
 * 浏览器接管下载过程，右上角显示下载进度。
 * 支持 http(s): 与 blob: 两种协议，blob: 下载完成后会自动 revoke。
 */
export function downloadFile(options: DownloadOptions): void {
    const {forceAttachment, url, fileName} = options;

    const isBlob = url.startsWith('blob:');
    if (!isBlob && !isValidDownloadUrl(url)) {
        message.error('该资源暂时无法下载');
        return;
    }

    const resolvedFileName = resolveFileName(url, fileName);
    const anchor = document.createElement('a');
    // blob: 是同源资源，不需要也不支持 responseContentDisposition
    anchor.href = isBlob ? url : createDownloadUrl(url, resolvedFileName, forceAttachment);
    anchor.download = resolvedFileName;
    anchor.style.display = 'none';
    anchor.rel = 'noopener noreferrer';

    document.body.appendChild(anchor);
    try {
        if (!isBlob) {
            skipBeforeUnloadOnce();
        }
        anchor.click();
    } finally {
        document.body.removeChild(anchor);
        if (isBlob) {
            URL.revokeObjectURL(url);
        }
    }
}
